/**
 * COSMO SEARCH — Frosted Light Glass Global Vault Omnibar & Draggable Hub
 * Scoped exclusively to 8 core vault templates:
 * 1. Payment Gateways
 * 2. Financial Credentials
 * 3. DNS Management
 * 4. Developer Endpoints
 * 5. Node Infrastructure
 * 6. Workspace Integration
 * 7. Merchant Identities
 * 8. System Encryption
 */

(function () {
    'use strict';

    const SCOPED_VAULTS = [
        { key: 'payment_gateways', slug: 'payment-gateways', name: 'Payment Gateways', icon: '💳' },
        { key: 'financial_credentials', slug: 'financial-credentials', name: 'Financial Credentials', icon: '🏦' },
        { key: 'dns_management', slug: 'dns-management', name: 'DNS Management', icon: '🌐' },
        { key: 'developer_endpoints', slug: 'developer-endpoints', name: 'Developer Endpoints', icon: '🔑' },
        { key: 'node_infrastructure', slug: 'node-infrastructure', name: 'Node Infrastructure', icon: '🖥️' },
        { key: 'workspace_integration', slug: 'workspace-integration', name: 'Workspace Integration', icon: '🏢' },
        { key: 'merchant_identities', slug: 'merchant-identities', name: 'Merchant Identities', icon: '🛍️' },
        { key: 'system_encryption', slug: 'system-encryption', name: 'System Encryption', icon: '🔒' }
    ];

    function resolveEnabledState() {
        const stored = localStorage.getItem('cosmo_search_enabled');
        if (stored === 'true') return true;
        if (stored === 'false') return false;
        const ud = window.userData || (window.parent && window.parent.userData) || (window.top && window.top.userData);
        if (ud && ud.choices && ud.choices.cosmo_search !== undefined) {
            return !!ud.choices.cosmo_search;
        }
        if (ud && ud.settings && ud.settings.cosmo_search_enabled !== undefined) {
            return ud.settings.cosmo_search_enabled === 'true' || ud.settings.cosmo_search_enabled === true;
        }
        // Disabled / removed by default on 1st time open
        return false;
    }

    let isEnabled = resolveEnabledState();
    let isModalOpen = false;
    let activeCategory = 'all';
    let selectedIndex = 0;
    let currentResults = [];

    // Inject Frosted Light Glass CSS
    const styleEl = document.createElement('style');
    styleEl.id = 'cosmo-search-styles';
    styleEl.textContent = `
        /* ── COSMO SEARCH FROSTED LIGHT GLASS THEME (Zero Cyberpunk, Pure Acrylic) ── */
        #cosmo-search-orb {
            position: fixed !important;
            z-index: 2147483647 !important;
            width: 52px;
            height: 52px;
            border-radius: 50%;
            background: linear-gradient(135deg, rgba(255, 255, 255, 0.94), rgba(240, 249, 255, 0.88));
            backdrop-filter: blur(18px) saturate(180%);
            -webkit-backdrop-filter: blur(18px) saturate(180%);
            border: 1.5px solid rgba(255, 255, 255, 0.95);
            box-shadow: 0 10px 30px -4px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(186, 230, 253, 0.7), 0 0 20px rgba(14, 165, 233, 0.2);
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: grab;
            user-select: none;
            touch-action: none;
            transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.2s ease;
        }
        body.drawer-open #cosmo-search-orb,
        body.modal-open #cosmo-search-orb,
        body.nav-modal-open #cosmo-search-orb,
        .drawer-open #cosmo-search-orb,
        #cosmo-search-orb.force-hidden {
            display: none !important;
            opacity: 0 !important;
            visibility: hidden !important;
            pointer-events: none !important;
        }
        @media (max-width: 768px) {
            #cosmo-search-orb {
                width: 50px !important;
                height: 50px !important;
            }
        }
        #cosmo-search-orb.is-dragging {
            cursor: grabbing !important;
            transform: scale(1.05) !important;
            box-shadow: 0 16px 38px -4px rgba(0, 0, 0, 0.2), 0 0 0 1.5px rgba(56, 189, 248, 0.9), 0 0 28px rgba(14, 165, 233, 0.4) !important;
        }
        #cosmo-search-orb img,
        #cosmo-search-orb svg {
            width: 28px;
            height: 28px;
            object-fit: contain;
            pointer-events: none;
            user-select: none;
            transition: transform 0.22s ease;
        }
        @media (hover: hover) and (pointer: fine) {
            #cosmo-search-orb:hover:not(.is-dragging) {
                box-shadow: 0 14px 35px -4px rgba(0, 0, 0, 0.16), 0 0 0 1.5px rgba(56, 189, 248, 0.85), 0 0 25px rgba(14, 165, 233, 0.35);
            }
            #cosmo-search-orb:hover:not(.is-dragging) img {
                transform: scale(1.1);
            }
        }

        /* Desktop Orb Tooltip */
        #cosmo-search-orb::after {
            content: 'Cosmo Search (Ctrl+K)';
            position: absolute;
            right: 60px;
            white-space: nowrap;
            background: rgba(15, 23, 42, 0.88);
            color: #f8fafc;
            font-size: 11px;
            font-family: 'Space Grotesk', system-ui, sans-serif;
            font-weight: 600;
            padding: 5px 10px;
            border-radius: 8px;
            backdrop-filter: blur(10px);
            opacity: 0;
            pointer-events: none;
            transform: translateX(6px);
            transition: all 0.2s ease;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        @media (hover: hover) and (min-width: 768px) {
            #cosmo-search-orb:hover::after {
                opacity: 1;
                transform: translateX(0);
            }
        }

        /* ── OMNIBAR MODAL OVERLAY (LIGHT FROST BLUR) ── */
        #cosmo-search-overlay {
            position: fixed;
            inset: 0;
            z-index: 1000000;
            background: rgba(15, 23, 42, 0.28);
            backdrop-filter: blur(24px) saturate(180%);
            -webkit-backdrop-filter: blur(24px) saturate(180%);
            display: none;
            align-items: flex-start;
            justify-content: center;
            padding: 80px 16px 24px 16px;
            opacity: 0;
            pointer-events: none;
            transition: opacity 0.25s cubic-bezier(0.16, 1, 0.3, 1);
        }
        #cosmo-search-overlay.open {
            display: flex !important;
            opacity: 1;
            pointer-events: auto;
        }

        /* ── UNAUTHENTICATED LOCK STATE ── */
        .cs-unauth-box {
            padding: 34px 20px 28px 20px;
            text-align: center;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
        }
        .cs-unauth-icon-wrap {
            width: 58px;
            height: 58px;
            border-radius: 18px;
            background: linear-gradient(135deg, rgba(239, 68, 68, 0.14), rgba(244, 63, 94, 0.08));
            border: 1.5px solid rgba(239, 68, 68, 0.35);
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 14px;
            box-shadow: 0 10px 25px -5px rgba(239, 68, 68, 0.2);
        }
        .cs-unauth-lock-icon {
            font-size: 28px !important;
            color: #ef4444;
        }
        .cs-unauth-title {
            font-size: 15px;
            font-weight: 700;
            color: #0f172a;
            letter-spacing: -0.01em;
            margin-bottom: 6px;
            text-transform: uppercase;
        }
        .cs-unauth-desc {
            font-size: 13.5px;
            font-weight: 600;
            color: #dc2626;
            max-width: 460px;
            line-height: 1.45;
            margin-bottom: 6px;
        }
        .cs-unauth-subdesc {
            font-size: 12px;
            color: #64748b;
            max-width: 420px;
            line-height: 1.45;
            margin-bottom: 20px;
        }
        .cs-unauth-btn {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 9px 22px;
            border-radius: 12px;
            background: linear-gradient(135deg, #0284c7, #0ea5e9);
            color: #ffffff;
            font-size: 13px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            box-shadow: 0 6px 18px -2px rgba(14, 165, 233, 0.35);
            transition: all 0.2s ease;
            user-select: none;
            font-family: inherit;
        }
        .cs-unauth-btn:hover {
            transform: translateY(-1px);
            box-shadow: 0 8px 24px -2px rgba(14, 165, 233, 0.45);
        }
        .cs-unauth-btn:active {
            transform: scale(0.97);
        }
        .cs-unauth-btn span.material-symbols-outlined {
            font-size: 18px !important;
        }

        /* ── MODAL CONTAINER ── */
        #cosmo-search-box {
            width: 100%;
            max-width: 680px;
            background: linear-gradient(135deg, rgba(255, 255, 255, 0.94), rgba(240, 249, 255, 0.9));
            backdrop-filter: blur(28px) saturate(190%);
            -webkit-backdrop-filter: blur(28px) saturate(190%);
            border: 1.5px solid rgba(255, 255, 255, 0.95);
            box-shadow: 0 25px 65px -15px rgba(15, 23, 42, 0.16), 0 0 0 1px rgba(186, 230, 253, 0.6), 0 8px 30px rgba(14, 165, 233, 0.12);
            border-radius: 24px;
            overflow: hidden;
            display: flex;
            flex-direction: column;
            transform: scale(0.96) translateY(-14px);
            transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1);
            font-family: 'Space Grotesk', system-ui, sans-serif;
        }
        #cosmo-search-overlay.open #cosmo-search-box {
            transform: scale(1) translateY(0);
        }

        /* Input Header */
        .cs-header {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 16px 20px;
            border-bottom: 1px solid rgba(226, 232, 240, 0.8);
            background: rgba(255, 255, 255, 0.6);
        }
        .cs-search-icon {
            width: 22px;
            height: 22px;
            object-fit: contain;
            flex-shrink: 0;
            pointer-events: none;
            user-select: none;
        }
        .cs-input {
            flex: 1;
            background: transparent;
            border: none;
            outline: none;
            color: #0f172a;
            font-size: 16px;
            font-weight: 500;
            font-family: inherit;
        }
        .cs-input::placeholder {
            color: #94a3b8;
            font-weight: 400;
        }
        .cs-esc-badge {
            font-size: 11px;
            font-weight: 700;
            color: #64748b;
            background: rgba(241, 245, 249, 0.9);
            border: 1px solid rgba(203, 213, 225, 0.8);
            padding: 3px 8px;
            border-radius: 6px;
            cursor: pointer;
            user-select: none;
        }

        /* Filter Pills Row */
        .cs-filters {
            display: flex;
            gap: 6px;
            padding: 10px 18px;
            overflow-x: auto;
            scrollbar-width: none;
            background: rgba(248, 250, 252, 0.7);
            border-bottom: 1px solid rgba(226, 232, 240, 0.6);
        }
        .cs-filters::-webkit-scrollbar {
            display: none;
        }
        .cs-pill {
            display: inline-flex;
            align-items: center;
            gap: 5px;
            padding: 5px 12px;
            border-radius: 20px;
            font-size: 11.5px;
            font-weight: 600;
            color: #475569;
            background: rgba(255, 255, 255, 0.85);
            border: 1px solid rgba(226, 232, 240, 0.85);
            cursor: pointer;
            white-space: nowrap;
            transition: all 0.2s ease;
            user-select: none;
        }
        .cs-pill:hover {
            color: #0284c7;
            border-color: rgba(186, 230, 253, 0.9);
            background: rgba(240, 249, 255, 0.9);
        }
        .cs-pill.active {
            color: #ffffff;
            background: linear-gradient(135deg, #0284c7, #0ea5e9);
            border-color: transparent;
            box-shadow: 0 4px 12px rgba(14, 165, 233, 0.25);
        }

        /* Results List Container */
        .cs-results {
            max-height: 380px;
            overflow-y: auto;
            padding: 10px 14px;
            display: flex;
            flex-direction: column;
            gap: 6px;
        }
        .cs-results::-webkit-scrollbar {
            width: 6px;
        }
        .cs-results::-webkit-scrollbar-thumb {
            background: rgba(203, 213, 225, 0.7);
            border-radius: 4px;
        }

        /* Result Item Row */
        .cs-item {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 10px 14px;
            border-radius: 14px;
            background: rgba(255, 255, 255, 0.65);
            border: 1px solid rgba(226, 232, 240, 0.7);
            cursor: pointer;
            transition: all 0.18s ease;
            text-decoration: none;
            color: inherit;
        }
        .cs-item:hover, .cs-item.selected {
            background: rgba(224, 242, 254, 0.8);
            border-color: rgba(125, 211, 252, 0.9);
            transform: translateX(2px);
            box-shadow: 0 4px 14px rgba(14, 165, 233, 0.1);
        }
        .cs-item-left {
            display: flex;
            align-items: center;
            gap: 12px;
            min-width: 0;
            flex: 1;
        }
        .cs-item-icon-box {
            width: 34px;
            height: 34px;
            border-radius: 10px;
            background: rgba(240, 249, 255, 0.9);
            border: 1px solid rgba(186, 230, 253, 0.8);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 16px;
            flex-shrink: 0;
        }
        .cs-item-info {
            min-width: 0;
            flex: 1;
        }
        .cs-item-title {
            font-size: 13.5px;
            font-weight: 600;
            color: #0f172a;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .cs-item-subtitle {
            font-size: 11.5px;
            color: #64748b;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            margin-top: 1px;
        }
        .cs-item-badge {
            font-size: 10.5px;
            font-weight: 700;
            color: #0369a1;
            background: rgba(224, 242, 254, 0.9);
            border: 1px solid rgba(186, 230, 253, 0.85);
            padding: 3px 8px;
            border-radius: 6px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            flex-shrink: 0;
            margin-left: 10px;
        }

        /* Empty / Zero-State */
        .cs-empty {
            padding: 40px 20px;
            text-align: center;
            color: #64748b;
            font-size: 13.5px;
        }
        .cs-empty-icon {
            font-size: 28px;
            margin-bottom: 8px;
            opacity: 0.7;
        }

        /* Footer Info */
        .cs-footer {
            padding: 8px 18px;
            background: rgba(248, 250, 252, 0.85);
            border-top: 1px solid rgba(226, 232, 240, 0.7);
            display: flex;
            align-items: center;
            justify-content: space-between;
            font-size: 11px;
            color: #64748b;
        }
        .cs-footer kbd {
            font-family: inherit;
            background: rgba(255, 255, 255, 0.9);
            border: 1px solid rgba(203, 213, 225, 0.8);
            padding: 2px 5px;
            border-radius: 4px;
            font-weight: 700;
            color: #334155;
        }

        /* Mobile specific styling */
        @media (max-width: 640px) {
            #cosmo-search-overlay {
                padding: 16px;
                align-items: center;
            }
            #cosmo-search-box {
                max-height: 85vh;
                border-radius: 20px;
            }
            .cs-footer {
                display: none;
            }
        }
    `;
    document.head.appendChild(styleEl);

    // Create Orb Element
    const orb = document.createElement('div');
    orb.id = 'cosmo-search-orb';
    orb.setAttribute('role', 'button');
    orb.setAttribute('aria-label', 'Open Cosmo Search');
    orb.innerHTML = `
        <img class="cs-orb-icon" src="https://cdn.cosmocrypt.com/templates-images/cosmo_search.png" alt="Cosmo Search" draggable="false" />
    `;

    // Restore Orb Position with mobile and desktop awareness
    function applyInitialOrbPosition() {
        const isMobile = window.innerWidth <= 768;
        const storageKey = isMobile ? 'cosmo_search_orb_pos_mobile' : 'cosmo_search_orb_pos';
        const savedPos = JSON.parse(localStorage.getItem(storageKey) || 'null');
        if (savedPos && typeof savedPos.x === 'number' && typeof savedPos.y === 'number') {
            const maxX = window.innerWidth - (isMobile ? 54 : 60);
            const maxY = window.innerHeight - (isMobile ? 115 : 60);
            orb.style.left = `${Math.min(maxX, Math.max(10, savedPos.x))}px`;
            orb.style.top = `${Math.min(maxY, Math.max(10, savedPos.y))}px`;
            orb.style.bottom = 'auto';
            orb.style.right = 'auto';
        } else {
            orb.style.left = 'auto';
            orb.style.top = 'auto';
            if (isMobile) {
                orb.style.bottom = '105px';
                orb.style.right = '18px';
            } else {
                orb.style.bottom = '28px';
                orb.style.right = '28px';
            }
        }
    }
    applyInitialOrbPosition();
    window.addEventListener('resize', applyInitialOrbPosition);

    // Initial check (hidden on About page)
    updateOrbVisibility();
    document.body.appendChild(orb);

    // Create Omnibar Modal Element
    const overlay = document.createElement('div');
    overlay.id = 'cosmo-search-overlay';
    overlay.innerHTML = `
        <div id="cosmo-search-box">
            <div class="cs-header">
                <img class="cs-search-icon" src="https://cdn.cosmocrypt.com/templates-images/cosmo_search.png" alt="Cosmo Search" draggable="false" />
                <input type="search" class="cs-input" id="cs-search-input" placeholder="Search 8 core vaults (DNS, Nodes, Workspace, APIs...)" autocomplete="off" autocorrect="off" spellcheck="false">
                <span class="cs-esc-badge" id="cs-esc-btn">ESC</span>
            </div>
            <div class="cs-filters" id="cs-filters-container">
                <button type="button" class="cs-pill active" data-filter="all">All (8)</button>
                ${SCOPED_VAULTS.map(v => `<button type="button" class="cs-pill" data-filter="${v.key}">${v.icon} ${v.name.split(' ')[0]}</button>`).join('')}
            </div>
            <div class="cs-results" id="cs-results-container">
                <div class="cs-empty">
                    <div class="cs-empty-icon"><img src="https://cdn.cosmocrypt.com/templates-images/cosmo_search.png" alt="Search" style="width: 32px; height: 32px; object-fit: contain; opacity: 0.85; margin: 0 auto 8px auto; display: block;" draggable="false" /></div>
                    Type any keyword to search across the 8 core vaults.
                </div>
            </div>
            <div class="cs-footer">
                <span>Navigate: <kbd>↑</kbd> <kbd>↓</kbd></span>
                <span>Select: <kbd>↵</kbd></span>
                <span>Close: <kbd>ESC</kbd></span>
                <span class="cs-footer-status" style="color: #0284c7; font-weight: 600;">8 Vaults In-Memory Search</span>
            </div>
        </div>
    `;
    document.body.appendChild(overlay);

    const searchInput = document.getElementById('cs-search-input');
    const resultsContainer = document.getElementById('cs-results-container');
    const filtersContainer = document.getElementById('cs-filters-container');
    const escBtn = document.getElementById('cs-esc-btn');

    // ── DRAG & DROP LOGIC WITH MAGNETIC EDGE SNAP & CLICK THRESHOLD ──
    // ── DRAG & DROP LOGIC WITH MAGNETIC EDGE SNAP & ZERO-HOVER SAFETY ──
    let isPointerDown = false;
    let isDragging = false;
    let hasMoved = false;
    let dragStartX = 0;
    let dragStartY = 0;
    let orbStartX = 0;
    let orbStartY = 0;
    let activePointerId = null;

    function resetOrbDrag() {
        if (activePointerId !== null) {
            try {
                orb.releasePointerCapture(activePointerId);
            } catch (e) { }
        }
        isPointerDown = false;
        isDragging = false;
        hasMoved = false;
        activePointerId = null;
        orb.classList.remove('is-dragging');
        orb.style.transition = 'left 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), top 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)';
    }

    orb.setAttribute('draggable', 'false');
    orb.addEventListener('dragstart', (e) => e.preventDefault());

    // Pointer Down: Only starts on genuine click / touch
    orb.addEventListener('pointerdown', (e) => {
        // Only accept primary button (left click) or touch
        if (e.pointerType === 'mouse' && e.button !== 0) return;

        isPointerDown = true;
        isDragging = false;
        hasMoved = false;
        activePointerId = e.pointerId;
        dragStartX = e.clientX;
        dragStartY = e.clientY;

        const rect = orb.getBoundingClientRect();
        orbStartX = rect.left;
        orbStartY = rect.top;

        try {
            orb.setPointerCapture(e.pointerId);
        } catch (err) { }

        e.preventDefault();
    });

    // Pointer Move: ONLY active when mouse button or finger is actively held down
    orb.addEventListener('pointermove', (e) => {
        if (!isPointerDown || e.pointerId !== activePointerId) return;

        // Safety check for mouse: if primary button is not held down, cancel immediately
        if (e.pointerType === 'mouse' && (e.buttons & 1) === 0) {
            resetOrbDrag();
            return;
        }

        const deltaX = e.clientX - dragStartX;
        const deltaY = e.clientY - dragStartY;
        const distance = Math.hypot(deltaX, deltaY);

        // Require minimum 10px intentional drag distance so a click/tap never drags
        if (!hasMoved && distance > 10) {
            hasMoved = true;
            isDragging = true;
            orb.classList.add('is-dragging');
            orb.style.transition = 'none';
        }

        if (hasMoved) {
            let nextX = orbStartX + deltaX;
            let nextY = orbStartY + deltaY;

            const isMobile = window.innerWidth <= 768;
            const maxX = window.innerWidth - (isMobile ? 54 : 60);
            const maxY = window.innerHeight - (isMobile ? 115 : 60);

            nextX = Math.max(10, Math.min(maxX, nextX));
            nextY = Math.max(10, Math.min(maxY, nextY));

            orb.style.left = `${nextX}px`;
            orb.style.top = `${nextY}px`;
            orb.style.bottom = 'auto';
            orb.style.right = 'auto';
        }
    });

    // Pointer Up: Release click or finger lift
    orb.addEventListener('pointerup', (e) => {
        if (e.pointerId !== activePointerId && activePointerId !== null) return;

        if (activePointerId !== null) {
            try {
                orb.releasePointerCapture(activePointerId);
            } catch (err) { }
        }

        const wasDragging = hasMoved;
        const wasPointerDown = isPointerDown;

        isPointerDown = false;
        isDragging = false;
        hasMoved = false;
        activePointerId = null;
        orb.classList.remove('is-dragging');
        orb.style.transition = 'left 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), top 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)';

        if (!wasPointerDown) return;

        if (wasDragging) {
            // Magnetic edge snap to nearest horizontal side
            const isMobile = window.innerWidth <= 768;
            const rect = orb.getBoundingClientRect();
            const centerX = rect.left + rect.width / 2;
            const targetX = centerX < window.innerWidth / 2 ? 16 : (window.innerWidth - rect.width - 16);
            const minY = 16;
            const maxY = window.innerHeight - rect.height - (isMobile ? 105 : 16);
            const targetY = Math.max(minY, Math.min(maxY, rect.top));

            orb.style.left = `${targetX}px`;
            orb.style.top = `${targetY}px`;
            orb.style.bottom = 'auto';
            orb.style.right = 'auto';
            const storageKey = isMobile ? 'cosmo_search_orb_pos_mobile' : 'cosmo_search_orb_pos';
            try {
                localStorage.setItem(storageKey, JSON.stringify({ x: targetX, y: targetY }));
            } catch (e) { }
        } else {
            // Clean tap or click without dragging -> Open Cosmo Search!
            window.openCosmoSearch();
        }
    });

    orb.addEventListener('pointercancel', resetOrbDrag);
    orb.addEventListener('lostpointercapture', resetOrbDrag);
    window.addEventListener('blur', resetOrbDrag);
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) resetOrbDrag();
    });

    // ── AUTHENTICATION CHECK HELPER ──
    function checkUserLoggedIn() {
        try {
            const userEnc = sessionStorage.getItem('userStoreEnc') || localStorage.getItem('userStoreEnc');
            if (userEnc && userEnc.length > 20) return true;
            const ud = window.userData || (window.parent && window.parent.userData) || (window.top && window.top.userData);
            if (ud && (ud.email || ud.uid || ud.isVerified || ud.name || ud.first || ud.userName || ud.displayName)) return true;
            const pAuth = window.parent && (window.parent.suiteAuth || window.parent.auth);
            if (pAuth && pAuth.currentUser && (pAuth.currentUser.email || pAuth.currentUser.uid)) return true;
            const wAuth = (window.suiteAuth || window.auth);
            if (wAuth && wAuth.currentUser && (wAuth.currentUser.email || wAuth.currentUser.uid)) return true;
            const tAuth = window.top && (window.top.suiteAuth || window.top.auth);
            if (tAuth && tAuth.currentUser && (tAuth.currentUser.email || tAuth.currentUser.uid)) return true;
        } catch (e) { }
        return false;
    }

    function renderUnauthenticatedNotice() {
        resultsContainer.innerHTML = `
            <div class="cs-unauth-box">
                <div class="cs-unauth-icon-wrap">
                    <span class="material-symbols-outlined cs-unauth-lock-icon">lock</span>
                </div>
                <div class="cs-unauth-title">Authentication Required</div>
                <div class="cs-unauth-desc">
                    Search does not work until you login to see your data from the server.
                </div>
                <div class="cs-unauth-subdesc">
                    All credentials, vault nodes, and records are securely synced from the server. Sign in to your account to search across the 8 core vaults.
                </div>
                <button type="button" class="cs-unauth-btn" id="cs-unauth-login-btn">
                    <span class="material-symbols-outlined">login</span>
                    <span>Login to See Your Data</span>
                </button>
            </div>
        `;

        const footerStatus = overlay.querySelector('.cs-footer-status');
        if (footerStatus) {
            footerStatus.innerHTML = '<span style="color: #ef4444; font-weight: 600; display: inline-flex; align-items: center; gap: 4px;"><span class="material-symbols-outlined" style="font-size: 13px;">lock</span> Login Required</span>';
        }

        const loginBtn = document.getElementById('cs-unauth-login-btn');
        if (loginBtn) {
            loginBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                window.closeCosmoSearch();
                if (window.parent && typeof window.parent.switchView === 'function') {
                    window.parent.switchView('account');
                } else if (typeof window.switchView === 'function') {
                    window.switchView('account');
                } else {
                    window.location.href = '/account';
                }
            });
        }
    }

    // ── IN-MEMORY 8-VAULT SEARCH ENGINE ──
    function performSearch() {
        const loggedIn = checkUserLoggedIn();
        if (!loggedIn) {
            selectedIndex = 0;
            currentResults = [];
            renderUnauthenticatedNotice();
            return;
        }

        const footerStatus = overlay.querySelector('.cs-footer-status');
        if (footerStatus) {
            footerStatus.innerHTML = '<span style="color: #0284c7; font-weight: 600;">8 Vaults In-Memory Search</span>';
        }

        const query = (searchInput.value || '').trim().toLowerCase();
        selectedIndex = 0;
        currentResults = [];

        // If search input is empty, do not show cards by default
        if (!query) {
            renderResults('');
            return;
        }

        // Check in-memory store
        const ud = window.userData || (window.parent && window.parent.userData) || (window.top && window.top.userData) || {};
        const tv = ud.template_vaults || {};
        const store = window.CosmoTemplateStore || (window.parent && window.parent.CosmoTemplateStore) || (window.top && window.top.CosmoTemplateStore);

        SCOPED_VAULTS.forEach(v => {
            if (activeCategory !== 'all' && activeCategory !== v.key) return;

            let cards = tv[v.key];
            if (!cards && tv[v.slug]) cards = tv[v.slug];
            if (!cards && tv[v.key.replace(/_/g, '-')]) cards = tv[v.key.replace(/_/g, '-')];
            if (!cards && store && typeof store.get === 'function') {
                cards = store.get(v.key, null);
            }

            if (cards && typeof cards === 'object' && !Array.isArray(cards) && Array.isArray(cards.data)) {
                cards = cards.data;
            }

            if (cards && Array.isArray(cards)) {
                cards.forEach((c, idx) => {
                    if (!c || typeof c !== 'object') return;

                    // Extract searchable labels
                    const title = (c.name || c.domain || c.title || c.label || c.nodeName || c.provider || c.accountName || c.merchantName || c.keyName || c.apiName || c.service || `Item #${idx + 1}`).trim();
                    const subtitle = (c.ip || c.host || c.url || c.endpoint || c.email || c.username || c.env || c.status || c.type || c.category || c.notes || '').trim();
                    const allText = Object.values(c)
                        .filter(val => typeof val === 'string' || typeof val === 'number')
                        .join(' ')
                        .toLowerCase();

                    if (allText.includes(query) || title.toLowerCase().includes(query) || subtitle.toLowerCase().includes(query)) {
                        currentResults.push({
                            vault: v,
                            title,
                            subtitle: subtitle || 'Active credentials',
                            cardIndex: idx,
                            cardData: c
                        });
                    }
                });
            }
        });

        renderResults(query);
    }

    function renderResults(query) {
        if (!query) {
            resultsContainer.innerHTML = `
                <div class="cs-empty">
                    <div class="cs-empty-icon"><img src="https://cdn.cosmocrypt.com/templates-images/cosmo_search.png" alt="Search" style="width: 32px; height: 32px; object-fit: contain; opacity: 0.85; margin: 0 auto 8px auto; display: block;" draggable="false" /></div>
                    Type any keyword to search across the 8 core vaults.
                </div>
            `;
            return;
        }

        if (currentResults.length === 0) {
            resultsContainer.innerHTML = `
                <div class="cs-empty">
                    <div class="cs-empty-icon">🚫</div>
                    No matches found for "<strong>${query.replace(/</g, '&lt;')}</strong>" in the 8 vaults.
                </div>
            `;
            return;
        }

        resultsContainer.innerHTML = currentResults.map((res, i) => `
            <div class="cs-item ${i === selectedIndex ? 'selected' : ''}" data-idx="${i}">
                <div class="cs-item-left">
                    <div class="cs-item-icon-box">${res.vault.icon}</div>
                    <div class="cs-item-info">
                        <div class="cs-item-title">${res.title.replace(/</g, '&lt;')}</div>
                        <div class="cs-item-subtitle">${res.subtitle.replace(/</g, '&lt;')}</div>
                    </div>
                </div>
                <div class="cs-item-badge">${res.vault.name}</div>
            </div>
        `).join('');

        // Attach click listeners to rows
        resultsContainer.querySelectorAll('.cs-item').forEach(itemEl => {
            itemEl.addEventListener('click', () => {
                const idx = parseInt(itemEl.getAttribute('data-idx'), 10);
                activateResult(idx);
            });
        });
    }

    function highlightIframeCard(doc, { cardTitle, cardIndex }) {
        if (!doc || !doc.body) return false;

        // Clear any previous active highlights across document
        try {
            doc.querySelectorAll('[data-cosmo-highlighted="true"]').forEach(el => {
                if (typeof el._cosmoClearHighlight === 'function') {
                    el._cosmoClearHighlight();
                }
            });
        } catch (e) { }

        let target = null;

        // 1. Try by ID first: card-0, card-1, etc.
        if (typeof cardIndex === 'number') {
            target = doc.getElementById(`card-${cardIndex}`);
        }

        // 2. Try searching by card classes
        if (!target) {
            const cards = doc.querySelectorAll('.c1-card, .card, .credential-card, [data-card-index], [id^="card-"]');
            if (cardTitle) {
                const cleanTarget = cardTitle.trim().toLowerCase();
                for (const c of cards) {
                    if (c.innerText && c.innerText.toLowerCase().includes(cleanTarget)) {
                        target = c;
                        break;
                    }
                }
            }
            if (!target && typeof cardIndex === 'number' && cards[cardIndex]) {
                target = cards[cardIndex];
            }
        }

        // 3. Fallback: text walker across DOM
        if (!target && cardTitle) {
            const cleanTarget = cardTitle.trim().toLowerCase();
            const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, null, false);
            let node;
            while ((node = walker.nextNode())) {
                if (node.nodeValue && node.nodeValue.trim().toLowerCase() === cleanTarget) {
                    target = node.parentElement.closest('.c1-card, .card, .credential-card, .glass-card, [data-card-index], tr, div[class*="card"]');
                    if (target) break;
                }
            }
        }

        if (!target) return false;

        try {
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            target.setAttribute('data-cosmo-highlighted', 'true');

            const origTransition = target.style.transition;
            const origBoxShadow = target.style.boxShadow;
            const origOutline = target.style.outline;

            target.style.transition = 'box-shadow 0.3s ease, outline 0.3s ease';
            target.style.boxShadow = '0 0 0 3.5px #0ea5e9, 0 10px 30px rgba(14, 165, 233, 0.45)';

            const highlightTime = Date.now();
            let cleanedUp = false;

            function removeHighlight() {
                if (cleanedUp) return;
                cleanedUp = true;
                if (highlightTimer) clearTimeout(highlightTimer);
                try {
                    target.removeAttribute('data-cosmo-highlighted');
                    target.style.boxShadow = origBoxShadow;
                    target.style.outline = origOutline;
                    target.style.transition = origTransition;
                } catch (e) { }
                try {
                    doc.removeEventListener('click', handleDismissClick);
                    window.removeEventListener('click', handleDismissClick);
                } catch (e) { }
            }

            function handleDismissClick() {
                // Must be at least 500ms after highlight was applied so the activating click doesn't trigger dismissal!
                if (Date.now() - highlightTime < 500) return;
                removeHighlight();
            }

            target._cosmoClearHighlight = removeHighlight;

            // 1. Auto-remove after exactly 3 seconds
            const highlightTimer = setTimeout(removeHighlight, 3000);

            // 2. Or remove when user clicks anywhere on page (attached after 500ms debounce)
            setTimeout(() => {
                doc.addEventListener('click', handleDismissClick, { once: true });
                window.addEventListener('click', handleDismissClick, { once: true });
            }, 500);

            return true;
        } catch (e) {
            return false;
        }
    }

    function activateResult(idx) {
        const item = currentResults[idx];
        if (!item) return;

        window.closeCosmoSearch();

        // 1. Switch to target module (handle transition from landing / home page)
        const landingOverlay = document.getElementById('landing-page-overlay');
        const isLandingVisible = landingOverlay && window.getComputedStyle(landingOverlay).display !== 'none';

        if (isLandingVisible) {
            if (typeof window.enterCosmoCryptApp === 'function') {
                window.enterCosmoCryptApp(item.vault.slug);
            } else if (typeof window.switchView === 'function') {
                window.switchView(item.vault.slug);
            }
            landingOverlay.style.transition = 'opacity 0.35s ease';
            landingOverlay.style.opacity = '0';
            setTimeout(() => {
                landingOverlay.style.display = 'none';
            }, 350);
            document.body.classList.remove('landing-menu-open');
        } else {
            if (typeof window.switchView === 'function') {
                window.switchView(item.vault.slug);
            } else if (typeof window.enterCosmoCryptApp === 'function') {
                window.enterCosmoCryptApp(item.vault.slug);
            }
        }

        // 2. Broadcast highlight card instruction with retries across iframe render
        const tryHighlight = () => {
            let found = false;
            document.querySelectorAll('.view-layer iframe, iframe').forEach(ifr => {
                try {
                    if (ifr.contentWindow) {
                        ifr.contentWindow.postMessage({
                            type: 'highlight-card',
                            cardTitle: item.title,
                            cardIndex: item.cardIndex
                        }, '*');
                    }
                    if (ifr.contentDocument) {
                        const res = highlightIframeCard(ifr.contentDocument, { cardTitle: item.title, cardIndex: item.cardIndex });
                        if (res) found = true;
                    }
                } catch (e) { }
            });
            return found;
        };

        // Execute and retry until template iframe finishes rendering cards
        setTimeout(() => {
            if (!tryHighlight()) {
                setTimeout(() => {
                    if (!tryHighlight()) {
                        setTimeout(() => {
                            if (!tryHighlight()) {
                                setTimeout(tryHighlight, 500);
                            }
                        }, 350);
                    }
                }, 300);
            }
        }, 200);
    }

    // Check if currently on Console, About, Sessions, or Preferences page/view (Cosmo Search orb excluded)
    function isExcludedView() {
        try {
            const path = (window.location.pathname || '').toLowerCase().replace(/^\/|\/$/g, '');
            const hash = (window.location.hash || '').toLowerCase();
            const href = (window.location.href || '').toLowerCase();

            // 1. Console exclusion (Console page / templates/console.html / console view)
            if (path === 'console' || path.endsWith('/console') || path.endsWith('console.html') || hash.includes('console') || href.includes('console.html') || href.includes('/templates/console')) {
                return true;
            }
            if (document.getElementById('rightConsolePanel') || document.getElementById('leftHeroPanel') || document.getElementById('heroTitle') || document.getElementById('consoleBannerWrapper')) {
                return true;
            }

            // 2. About exclusion
            if (path === 'about' || path.endsWith('/about') || path.endsWith('about.html') || hash === '#about') {
                return true;
            }
            // 3. Sessions exclusion
            if (path === 'sessions' || path.endsWith('/sessions') || path.endsWith('sessions.html') || hash === '#sessions') {
                return true;
            }
            // 4. Preferences exclusion
            if (path === 'preferences' || path.endsWith('/preferences') || path.endsWith('preferences.html') || path === 'settings' || path.endsWith('/settings') || hash === '#preferences' || hash === '#settings') {
                return true;
            }
            // 5. Legal / Privacy exclusion
            if (path === 'legal' || path.endsWith('/legal') || path.endsWith('legal.html') || hash === '#legal' || hash === '#privacy' || path === 'privacy' || path.endsWith('/privacy') || path.endsWith('privacy.html') || href.includes('legal.html') || href.includes('privacy.html') || href.includes('/templates/legal')) {
                return true;
            }

            const currentView = (window._currentSuiteViewState || sessionStorage.getItem('current_suite_view') || '').toLowerCase();
            if (currentView === 'console' || currentView === 'console.html') {
                return true;
            }
            if (currentView === 'about' || currentView === 'about.html') {
                return true;
            }
            if (currentView === 'sessions' || currentView === 'sessions.html') {
                return true;
            }
            if (currentView === 'preferences' || currentView === 'preferences.html' || currentView === 'settings' || currentView === 'setting.html') {
                return true;
            }
            if (currentView === 'legal' || currentView === 'legal.html' || currentView === 'privacy' || currentView === 'privacy.html') {
                return true;
            }

            const viewConsole = document.getElementById('view-console');
            if (viewConsole && (viewConsole.classList.contains('active') || viewConsole.style.display === 'block')) {
                return true;
            }
            const viewAbout = document.getElementById('view-about');
            if (viewAbout && (viewAbout.classList.contains('active') || viewAbout.style.display === 'block')) {
                return true;
            }
            const viewSessions = document.getElementById('view-sessions');
            if (viewSessions && (viewSessions.classList.contains('active') || viewSessions.style.display === 'block')) {
                return true;
            }
            const viewPreferences = document.getElementById('view-preferences') || document.getElementById('view-settings');
            if (viewPreferences && (viewPreferences.classList.contains('active') || viewPreferences.style.display === 'block')) {
                return true;
            }
            const viewLegal = document.getElementById('view-legal') || document.getElementById('view-privacy');
            if (viewLegal && (viewLegal.classList.contains('active') || viewLegal.style.display === 'block')) {
                return true;
            }

            // Check if active iframe is console or legal
            const activeIframe = document.querySelector('iframe.active, iframe#spa-iframe, iframe#main-view-frame, iframe#legal-frame, iframe#privacy-frame, iframe[style*="display: block"]');
            if (activeIframe && activeIframe.src && (activeIframe.src.toLowerCase().includes('console') || activeIframe.src.toLowerCase().includes('legal') || activeIframe.src.toLowerCase().includes('privacy'))) {
                return true;
            }

            // Check any visible iframe rendering legal content
            const allIframes = document.querySelectorAll('iframe');
            for (const ifr of allIframes) {
                try {
                    const parentLayer = ifr.closest('.view-layer');
                    const isVisible = (parentLayer && (parentLayer.classList.contains('active') || parentLayer.style.display === 'block')) ||
                                      (ifr.offsetParent !== null && ifr.style.display !== 'none');
                    if (isVisible) {
                        const src = (ifr.src || '').toLowerCase();
                        if (src.includes('legal') || src.includes('privacy')) return true;
                        if (ifr.id === 'legal-frame' || ifr.id === 'privacy-frame') return true;
                        if (ifr.contentDocument && (ifr.contentDocument.getElementById('legal-container') || ifr.contentDocument.getElementById('portal-index-card') || ifr.contentDocument.getElementById('group-panel-1'))) {
                            return true;
                        }
                    }
                } catch (e) { }
            }
        } catch (e) { }
        return false;
    }

    function isOrbHiddenByModalOrDrawer() {
        if (window._hideCosmoSearchOrb) return true;
        if (orb && orb.classList.contains('force-hidden')) return true;
        if (document.body && (
            document.body.classList.contains('drawer-open') || 
            document.body.classList.contains('modal-open') || 
            document.body.classList.contains('nav-modal-open')
        )) {
            return true;
        }
        try {
            const iframes = document.querySelectorAll('iframe');
            for (const ifr of iframes) {
                if (ifr.contentDocument && ifr.contentDocument.body) {
                    const b = ifr.contentDocument.body;
                    if (b.classList.contains('drawer-open') || 
                        b.classList.contains('modal-open') || 
                        b.classList.contains('nav-modal-open')) {
                        return true;
                    }
                    const modal = ifr.contentDocument.getElementById('desktopNavigationModal');
                    if (modal && modal.classList.contains('open')) {
                        return true;
                    }
                    const drawer = ifr.contentDocument.getElementById('navDrawer');
                    if (drawer && (drawer.classList.contains('translate-x-0') || !drawer.classList.contains('translate-x-full'))) {
                        return true;
                    }
                }
            }
        } catch (e) { }
        return false;
    }

    function updateOrbVisibility() {
        if (!isEnabled || isExcludedView() || isOrbHiddenByModalOrDrawer() || (orb && orb.classList.contains('force-hidden'))) {
            orb.style.display = 'none';
        } else {
            orb.style.display = 'flex';
        }
    }

    // ── PUBLIC API FOR OPENING / CLOSING ──
    window.openCosmoSearch = function () {
        if (isExcludedView()) return;
        isModalOpen = true;
        overlay.classList.add('open');
        searchInput.value = '';
        if (!checkUserLoggedIn()) {
            searchInput.placeholder = "Login required to search server vault data...";
        } else {
            searchInput.placeholder = "Search 8 core vaults (DNS, Nodes, Workspace, APIs...)";
        }
        searchInput.focus();
        performSearch();
    };

    window.closeCosmoSearch = function () {
        isModalOpen = false;
        overlay.classList.remove('open');
    };

    window.toggleCosmoSearch = function () {
        if (isExcludedView()) return;
        if (isModalOpen) window.closeCosmoSearch();
        else window.openCosmoSearch();
    };

    window.toggleCosmoSearchOrb = function (enable) {
        isEnabled = !!enable;
        localStorage.setItem('cosmo_search_enabled', isEnabled ? 'true' : 'false');
        updateOrbVisibility();
        if (!isEnabled) {
            window.closeCosmoSearch();
        }
    };

    // Watch view & history changes to auto-hide orb on About page
    window.addEventListener('popstate', updateOrbVisibility);
    window.addEventListener('hashchange', updateOrbVisibility);
    setInterval(updateOrbVisibility, 400);

    // Filter pill click listener
    filtersContainer.addEventListener('click', (e) => {
        const pill = e.target.closest('.cs-pill');
        if (!pill) return;
        filtersContainer.querySelectorAll('.cs-pill').forEach(p => p.classList.remove('active'));
        pill.classList.add('active');
        activeCategory = pill.getAttribute('data-filter') || 'all';
        performSearch();
    });

    searchInput.addEventListener('input', performSearch);
    escBtn.addEventListener('click', window.closeCosmoSearch);

    overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
            window.closeCosmoSearch();
        }
    });

    // Keyboard handlers (Ctrl+K, arrows, Enter, Esc) with CAPTURE PHASE
    window.addEventListener('keydown', (e) => {
        if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === 'k' || e.code === 'KeyK' || e.keyCode === 75)) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            window.toggleCosmoSearch();
            return false;
        }

        if (!isModalOpen) return;

        if (e.key === 'Escape') {
            e.preventDefault();
            window.closeCosmoSearch();
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (currentResults.length > 0) {
                selectedIndex = (selectedIndex + 1) % currentResults.length;
                renderResults(searchInput.value);
            }
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (currentResults.length > 0) {
                selectedIndex = (selectedIndex - 1 + currentResults.length) % currentResults.length;
                renderResults(searchInput.value);
            }
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (!checkUserLoggedIn()) {
                const loginBtn = document.getElementById('cs-unauth-login-btn');
                if (loginBtn) loginBtn.click();
                return;
            }
            if (currentResults.length > 0) {
                activateResult(selectedIndex);
            }
        }
    }, true); // Use capture phase to intercept before Chrome address bar

    // Attach listeners to child iframes for keydown forwarding and card highlighting
    function bindIframeEvents(iframe) {
        try {
            const handleKey = (e) => {
                if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === 'k' || e.code === 'KeyK' || e.keyCode === 75)) {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    window.toggleCosmoSearch();
                    return false;
                } else if (e.key === 'Escape' && isModalOpen) {
                    e.preventDefault();
                    window.closeCosmoSearch();
                }
            };

            if (iframe.contentWindow && !iframe.contentWindow._cosmoKeyBound) {
                iframe.contentWindow._cosmoKeyBound = true;
                iframe.contentWindow.addEventListener('keydown', handleKey, true);
                iframe.contentWindow.addEventListener('message', (e) => {
                    if (e.data && e.data.type === 'highlight-card' && iframe.contentDocument) {
                        highlightIframeCard(iframe.contentDocument, e.data);
                    }
                });
            }

            if (iframe.contentDocument && !iframe.contentDocument._cosmoKeyBound) {
                iframe.contentDocument._cosmoKeyBound = true;
                iframe.contentDocument.addEventListener('keydown', handleKey, true);
            }
        } catch (e) { }
    }

    function attachIframeListeners() {
        document.querySelectorAll('iframe').forEach(iframe => {
            bindIframeEvents(iframe);
            if (!iframe._cosmoLoadBound) {
                iframe._cosmoLoadBound = true;
                iframe.addEventListener('load', () => {
                    setTimeout(() => bindIframeEvents(iframe), 50);
                    setTimeout(() => bindIframeEvents(iframe), 350);
                    setTimeout(() => bindIframeEvents(iframe), 800);
                });
            }
        });
    }

    setInterval(attachIframeListeners, 800);
    attachIframeListeners();

    // Listen for toggle messages from Preferences iframe or child pages
    window.addEventListener('message', (e) => {
        if (e.data && e.data.type === 'cosmo-search-toggle') {
            window.toggleCosmoSearchOrb(e.data.enabled);
        } else if (e.data && e.data.type === 'cosmo-search-orb-visibility') {
            window._hideCosmoSearchOrb = !e.data.visible;
            if (orb) {
                if (!e.data.visible) {
                    orb.classList.add('force-hidden');
                    orb.style.setProperty('display', 'none', 'important');
                } else {
                    orb.classList.remove('force-hidden');
                    orb.style.removeProperty('display');
                }
            }
            updateOrbVisibility();
        } else if (e.data && e.data.type === 'forward-keydown') {
            if (e.data.key === 'k' && e.data.ctrlKey) {
                window.toggleCosmoSearch();
            }
        }
    });

    // Storage listener across tabs/windows
    window.addEventListener('storage', (e) => {
        if (e.key === 'cosmo_search_enabled') {
            window.toggleCosmoSearchOrb(e.newValue === 'true');
        }
    });

    window.addEventListener('safeStorageChange', (e) => {
        if (e.detail && e.detail.key === 'cosmo_search_enabled') {
            const val = e.detail.val !== undefined ? e.detail.val : e.detail.newValue;
            window.toggleCosmoSearchOrb(val === 'true' || val === true);
        }
    });

    // Sync from Firestore user data choices or settings if available
    function syncFromUserData() {
        const ud = window.userData || (window.parent && window.parent.userData) || (window.top && window.top.userData);
        if (ud) {
            let cloudVal = undefined;
            if (ud.choices && ud.choices.cosmo_search !== undefined) {
                cloudVal = !!ud.choices.cosmo_search;
            } else if (ud.settings && ud.settings.cosmo_search_enabled !== undefined) {
                cloudVal = ud.settings.cosmo_search_enabled !== 'false';
            }
            if (cloudVal !== undefined && localStorage.getItem('cosmo_search_enabled') === null) {
                window.toggleCosmoSearchOrb(cloudVal);
            }
        }
    }

    // ── REACTIVE AUTHENTICATION OBSERVER (INSTANT SYNC ON LOGIN / LOGOUT) ──
    let lastKnownLoginState = checkUserLoggedIn();

    function onAuthChangeDetected() {
        const isNowLoggedIn = checkUserLoggedIn();
        lastKnownLoginState = isNowLoggedIn;

        // Update placeholder dynamically
        if (!isNowLoggedIn) {
            searchInput.placeholder = "Login required to search server vault data...";
        } else {
            searchInput.placeholder = "Search 8 core vaults (DNS, Nodes, Workspace, APIs...)";
        }

        // If modal is open, immediately refresh the modal UI
        if (isModalOpen) {
            performSearch();
        }
    }

    function hookAuthStateObserver() {
        try {
            const auth = window.suiteAuth || (window.parent && window.parent.suiteAuth) || (window.top && window.top.suiteAuth) || window.auth || (window.parent && window.parent.auth);
            if (auth && typeof auth.onAuthStateChanged === 'function' && !auth.__cosmoSearchHooked) {
                auth.__cosmoSearchHooked = true;
                auth.onAuthStateChanged(() => {
                    setTimeout(onAuthChangeDetected, 40);
                });
            }
        } catch (e) { }
    }
    hookAuthStateObserver();
    setInterval(hookAuthStateObserver, 800);

    // Watch login & logout events across application windows, iframes, and local storage
    window.addEventListener('userDataUpdated', () => { syncFromUserData(); onAuthChangeDetected(); });
    window.addEventListener('cosmoProfileUpdated', () => { syncFromUserData(); onAuthChangeDetected(); });
    window.addEventListener('cosmoUserDataUpdated', () => { syncFromUserData(); onAuthChangeDetected(); });
    window.addEventListener('userLogin', onAuthChangeDetected);
    window.addEventListener('userLogout', onAuthChangeDetected);
    window.addEventListener('cosmoAuthChanged', onAuthChangeDetected);
    window.addEventListener('cosmoLoginSuccess', onAuthChangeDetected);
    window.addEventListener('authStateChanged', onAuthChangeDetected);

    try {
        if (window.parent && window.parent !== window) {
            window.parent.addEventListener('userDataUpdated', onAuthChangeDetected);
            window.parent.addEventListener('cosmoUserDataUpdated', onAuthChangeDetected);
            window.parent.addEventListener('userLogin', onAuthChangeDetected);
            window.parent.addEventListener('userLogout', onAuthChangeDetected);
            window.parent.addEventListener('authStateChanged', onAuthChangeDetected);
        }
    } catch (e) { }

    window.addEventListener('storage', (e) => {
        if (!e.key || e.key === 'userStoreEnc' || (typeof e.key === 'string' && (e.key.includes('authUser') || e.key.includes('userData') || e.key === 'user_subscription'))) {
            onAuthChangeDetected();
        }
    });

    window.addEventListener('safeStorageChange', (e) => {
        if (e.detail && (e.detail.key === 'userStoreEnc' || e.detail.key === 'userData' || e.detail.key === 'user_subscription')) {
            onAuthChangeDetected();
        }
    });

    // Make sure search modal is strictly closed by default on initial page load
    window.closeCosmoSearch();

})();
